/**
 * Owns one question: "is the stored session still good, and if not, what now?"
 *
 * This exists because an expired session used to leave the app wedged. The
 * stale token stayed in localStorage, every request kept failing with it, the
 * failures surfaced as an "Error detected" notification instead of a login
 * prompt, and the only way out was for the user to clear site data by hand.
 *
 * Deliberately imports nothing from the app. ApiClient and store both need it,
 * and store already imports ApiClient — so anything imported here would close
 * an import cycle. It talks to the rest of the app through a window event.
 */

export const SESSION_EXPIRED_EVENT = 'guardian:session-expired';

export const AUTH_KEY = 'auth';
export const USER_KEY = 'user';

export function authToken(): string | null {
  return localStorage.getItem(AUTH_KEY);
}

/** Drops every trace of the signed-in session from this browser. */
export function clearStoredSession(): void {
  localStorage.removeItem(AUTH_KEY);
  localStorage.removeItem(USER_KEY);
}

// One teardown per page load. Several requests fail together when a session
// dies — the dashboard fires organizations, monitors and current_user at once —
// and without this guard each one would separately clear state and navigate.
let expiring = false;

export function sessionIsExpiring(): boolean {
  return expiring;
}

export function notifySessionExpired(): void {
  if (expiring) return;
  expiring = true;

  clearStoredSession();
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

/** Test seam: lets a fresh login re-arm the guard above. */
export function resetSessionExpiry(): void {
  expiring = false;
}

/**
 * A 403 alone does not mean the session died — the API also answers 403 for
 * "you are signed in, but this is not yours" (the admin pages, another
 * organization's alert). Logging someone out because they opened a page they
 * lack rights to would be its own bug, so ask the one endpoint whose only
 * requirement is a valid session.
 *
 * A network failure returns false: being offline must never sign a clinician
 * out mid-shift.
 */
// Single-flight. When a session dies, the dashboard's parallel requests
// (organizations, monitors, current_user) all fail at once, and each would
// otherwise launch its own identical probe — three requests to answer one
// question, and three chances to be slow. They now share the first one.
let inFlightProbe: Promise<boolean> | null = null;

export async function sessionIsDead(apiUrl: string): Promise<boolean> {
  const token = authToken();
  if (!token) return true;

  if (!inFlightProbe) {
    inFlightProbe = probe(apiUrl).finally(() => {
      inFlightProbe = null;
    });
  }

  return inFlightProbe;
}

async function probe(apiUrl: string): Promise<boolean> {
  const token = authToken();
  if (!token) return true;

  // ApiClient awaits this probe before rethrowing, so an unbounded fetch here
  // would hold the original failure open — and with it the caller's loading
  // state — for as long as the connection stayed open without answering. That
  // is exactly the "spinner forever" this work set out to remove, reintroduced
  // one level down. AbortSignal.timeout is not in this TypeScript lib, hence
  // the explicit controller.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 10000);

  try {
    const response = await fetch(`${apiUrl}/api/current_user`, {
      headers: { Accept: 'application/json', auth: token },
      signal: controller.signal,
    });

    return response.status === 401 || response.status === 403;
  } catch {
    // Aborted or offline. "Cannot tell" errs toward keeping the user signed in.
    return false;
  } finally {
    clearTimeout(timer);
  }
}
